<script setup lang="ts">
import type { UserProfileTab, UserProfileTabItem } from './types'
import type { FrontendUserItem } from '@/api/modules/frontendUser'
import type { ApiRequestError } from '@/api/requestError'
import { FaButton, FaTabs } from '@fantastic-admin/components'
import { computed, h, shallowRef, watch } from 'vue'
import apiFrontendUser from '@/api/modules/frontendUser'
import { normalizeApiRequestError } from '@/api/requestError'
import AppEntityHeader from '@/components/AppDisplay/EntityHeader.vue'
import AppRequestState from '@/components/AppDisplay/RequestState.vue'
import { useUserDetail } from '../../composables/useUserDetail'
import { formatDateTime, maskPhone, profileCompleteness } from '../../utils/userDisplay'
import UserC2cTab from './UserC2cTab.vue'
import UserContentTab from './UserContentTab.vue'
import UserOverviewTab from './UserOverviewTab.vue'
import UserSafetyTab from './UserSafetyTab.vue'
import UserServiceTab from './UserServiceTab.vue'
import UserSocialTab from './UserSocialTab.vue'

defineOptions({ name: 'UserProfileDialog' })

const props = defineProps<{ userId: number }>()
const emit = defineEmits<{
  edit: [user: FrontendUserItem]
  openChat: [userId: number]
  updated: []
}>()
const visible = defineModel<boolean>({ required: true })
const activeTab = defineModel<UserProfileTab>('tab', { required: true })
const router = useRouter()
const { auth } = useAppAuth()
const actionLoading = shallowRef(false)
const actionError = shallowRef<ApiRequestError | null>(null)
const actionErrorTitle = shallowRef('用户操作未完成')
const {
  user,
  portrait,
  posts,
  goods,
  buyerOrders,
  sellerOrders,
  protocols,
  risks,
  deletions,
  avatarIdentityVerifications,
  conversations,
  tickets,
  detailLoading,
  tabLoading,
  detailError,
  tabError,
  serviceUnavailable,
  open,
  loadTab,
  reloadAvatarIdentityVerifications,
} = useUserDetail()

const tabs: UserProfileTabItem[] = [
  { value: 'overview', label: '概览' },
  { value: 'social', label: '社交' },
  { value: 'content', label: '内容' },
  { value: 'c2c', label: 'C2C' },
  { value: 'safety', label: '安全合规' },
  { value: 'service', label: '客服记录' },
]

const statusText = computed(() => Number(user.value?.status) === 1 ? '正常' : '禁用')
const statusTone = computed(() => Number(user.value?.status) === 1 ? 'success' as const : 'warning' as const)
const tags = computed(() => {
  if (!user.value) {
    return []
  }
  return [
    Number(user.value.is_high) === 1 ? '优质资料' : '',
    Number(user.value.is_new) === 1 ? '新人' : '',
    Number(user.value.is_recommend) === 1 ? '推荐中' : '',
    Number(user.value.is_logoff) === 1 ? '已注销' : '',
  ].filter(Boolean)
})
const metaItems = computed(() => user.value
  ? [
      { label: '用户 ID', value: user.value.id },
      { label: '联系方式', value: maskPhone(user.value.phone) },
      { label: '注册时间', value: formatDateTime(user.value.created_at) },
      { label: '最近登录', value: formatDateTime(user.value.last_login_time, '暂无') },
    ]
  : [])
const summaryItems = computed(() => user.value
  ? [
      { label: '资料完整度', value: `${profileCompleteness(user.value)}%`, tone: 'primary' as const },
      { label: '风险事件', value: portrait.value?.risk.risk_event_count ?? 0, tone: (portrait.value?.risk.risk_event_count ?? 0) > 0 ? 'warning' as const : 'success' as const },
    ]
  : [])
const canUpdate = computed(() => auth('admin:user.user:update'))
const canViewUserDetail = computed(() => auth('admin:user.user:detail'))
const canViewSafety = computed(() => auth('admin:safety.governance:list'))
const canViewSocialRelations = computed(() => auth('admin:social.profile:list'))
const canViewContent = computed(() => auth('admin:content.community.post:list'))
const canViewGoods = computed(() => auth('admin:goods.item:list'))
const canViewOrders = computed(() => auth('admin:trade.order:list'))
const canViewConversationHistory = computed(() => auth('admin:service.conversation.history:list'))
const canViewTickets = computed(() => auth('admin:service.ticket.ticket:list'))
const isEnabled = computed(() => Number(user.value?.status) === 1)

watch(
  [visible, () => props.userId],
  async ([isVisible, userId]) => {
    if (!isVisible || userId <= 0) {
      return
    }
    await open(userId)
  },
)

watch(activeTab, (tab) => {
  if (visible.value) {
    void loadTab(tab)
  }
})

function navigate(path: string, query: Record<string, number> = {}) {
  visible.value = false
  void router.push({
    path,
    query: Object.fromEntries(Object.entries({ user_id: props.userId, ...query }).map(([key, value]) => [key, String(value)])),
  })
}

async function refreshCurrentUser() {
  if (!user.value) {
    return
  }
  await open(user.value.id)
  emit('updated')
}

async function retryCurrentTab() {
  await loadTab(activeTab.value)
}

// 撤销真人头像认证后，仅刷新认证记录，并通知列表重新读取真人标识投影。
async function handleAvatarIdentityUpdated() {
  actionError.value = null
  try {
    await reloadAvatarIdentityVerifications()
    emit('updated')
  }
  catch (reason) {
    actionErrorTitle.value = '真人头像认证记录刷新失败'
    actionError.value = normalizeApiRequestError(reason)
  }
}

function editCurrentUser() {
  if (user.value) {
    emit('edit', user.value)
  }
}

function toggleUserStatus() {
  if (!user.value) {
    return
  }
  const nextStatus = isEnabled.value ? 2 : 1
  const actionLabel = isEnabled.value ? '禁用账号' : '启用账号'
  useFaModal().confirm({
    title: actionLabel,
    content: `确认${actionLabel}「${user.value.display_name || user.value.nickname || user.value.username}」吗？`,
    confirmButtonText: actionLabel,
    onConfirm: async () => {
      if (actionLoading.value) {
        return
      }
      actionLoading.value = true
      actionError.value = null
      try {
        await apiFrontendUser.updateStatus(user.value!.id, nextStatus)
      }
      catch (reason) {
        actionErrorTitle.value = `${actionLabel}失败`
        actionError.value = normalizeApiRequestError(reason)
        return
      }
      useFaToast().success(`${actionLabel}成功`)
      try {
        await refreshCurrentUser()
      }
      catch (reason) {
        actionErrorTitle.value = `${actionLabel}成功，但用户详情刷新失败`
        actionError.value = normalizeApiRequestError(reason)
      }
      finally {
        actionLoading.value = false
      }
    },
  })
}

function resetCurrentUserPassword() {
  if (!user.value) {
    return
  }
  useFaModal().confirm({
    title: '重置登录密码',
    content: `确认重置「${user.value.display_name || user.value.nickname || user.value.username}」的登录密码吗？`,
    confirmButtonText: '确认重置',
    onConfirm: async () => {
      if (actionLoading.value) {
        return
      }
      actionLoading.value = true
      actionError.value = null
      try {
        const response = await apiFrontendUser.resetPassword(user.value!.id)
        const password = response.data.password
        let copied = false
        try {
          await navigator.clipboard.writeText(password)
          copied = true
        }
        catch {
          copied = false
        }
        useFaModal().success({
          title: '密码已重置',
          description: copied ? '临时密码已复制，请通过安全渠道转交用户。' : '请通过安全渠道转交用户，并提醒首次登录后修改。',
          confirmButtonText: '知道了',
          content: h('div', { class: 'rounded-3 border border-primary/20 bg-primary/6 px-4 py-3 text-left' }, [
            h('div', { class: 'text-xs text-muted-foreground' }, '临时密码'),
            h('div', { class: 'mt-2 text-lg font-semibold text-primary' }, password),
          ]),
        })
      }
      catch (reason) {
        actionErrorTitle.value = '登录密码重置失败'
        actionError.value = normalizeApiRequestError(reason)
      }
      finally {
        actionLoading.value = false
      }
    },
  })
}
</script>

<template>
  <FaModal v-model="visible" title="用户详情" destroy-on-close class="sm:max-w-[1380px]" :footer="false">
    <div v-loading="detailLoading" class="min-h-100 space-y-4">
      <AppRequestState v-if="detailError" kind="error" title="用户详情加载失败" :description="detailError.message" :request-id="detailError.requestId" action-label="重新加载" @action="open(props.userId)" />
      <template v-else-if="user">
        <AppRequestState v-if="actionError" compact kind="error" :title="actionErrorTitle" :description="actionError.message" :request-id="actionError.requestId" />
        <AppEntityHeader
          :title="user.display_name || user.nickname || user.username"
          :subtitle="`@${user.username} · 前台用户完整资料`"
          :status-text="statusText"
          :status-tone="statusTone"
          :tags="tags"
          :meta-items="metaItems"
          :summary-items="summaryItems"
        >
          <template #actions>
            <div class="flex flex-wrap gap-2 justify-end">
              <FaButton v-if="canUpdate" variant="outline" :disabled="actionLoading" @click="editCurrentUser">
                编辑资料
              </FaButton>
              <FaButton v-if="canUpdate" variant="outline" :disabled="actionLoading" @click="toggleUserStatus">
                {{ isEnabled ? '禁用账号' : '启用账号' }}
              </FaButton>
              <FaButton v-if="canUpdate" variant="outline" :disabled="actionLoading" @click="resetCurrentUserPassword">
                重置密码
              </FaButton>
              <FaButton v-if="canViewUserDetail" variant="outline" @click="emit('openChat', user.id)">
                聊天记录
              </FaButton>
              <FaButton v-if="canViewSafety" variant="outline" @click="navigate('/safety/governance', { keyword: user.id })">
                进入安全治理
              </FaButton>
            </div>
          </template>
        </AppEntityHeader>

        <AppRequestState v-if="tabError" compact kind="error" title="当前用户页签加载失败" :description="tabError.message" :request-id="tabError.requestId" action-label="重新加载" @action="retryCurrentTab" />
        <FaTabs v-model="activeTab" :list="tabs" class="w-full" list-class="user-detail-tabs" content-class="pt-4">
          <template #overview>
            <UserOverviewTab :user="user" :portrait="portrait" />
          </template>
          <template #social>
            <UserSocialTab :user="user" :portrait="portrait" :can-navigate="canViewSocialRelations" @navigate="navigate('/social/user-quality')" />
          </template>
          <template #content>
            <div v-loading="tabLoading">
              <UserContentTab :posts="posts" :can-navigate="canViewContent" @navigate="navigate('/content/community/post')" />
            </div>
          </template>
          <template #c2c>
            <div v-loading="tabLoading">
              <UserC2cTab :user-id="user.id" :goods="goods" :buyer-orders="buyerOrders" :seller-orders="sellerOrders" :can-view-goods="canViewGoods" :can-view-orders="canViewOrders" @navigate="navigate" />
            </div>
          </template>
          <template #safety>
            <div v-loading="tabLoading">
              <UserSafetyTab
                :user-id="user.id"
                :protocols="protocols"
                :risks="risks"
                :deletions="deletions"
                :avatar-identity-verifications="avatarIdentityVerifications"
                :can-navigate="canViewSafety"
                :can-invalidate-avatar-identity="canUpdate"
                @navigate="navigate"
                @avatar-identity-updated="handleAvatarIdentityUpdated"
              />
            </div>
          </template>
          <template #service>
            <div v-loading="tabLoading">
              <UserServiceTab :conversations="conversations" :tickets="tickets" :unavailable="serviceUnavailable" :can-view-conversations="canViewConversationHistory" :can-view-tickets="canViewTickets" @navigate="navigate" />
            </div>
          </template>
        </FaTabs>
      </template>
    </div>
  </FaModal>
</template>

<style scoped>
:deep(.user-detail-tabs) {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  height: auto;
  padding: 0;
  overflow-x: auto;
  background: transparent;
  border-bottom: 1px solid var(--el-border-color-light);
  border-radius: 0;
}

:deep(.user-detail-tabs [data-slot="tabs-trigger"]) {
  flex: 0 0 auto;
  width: auto;
  height: 44px;
  padding: 0 18px;
  white-space: nowrap;
  background: transparent;
  border: 0;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  box-shadow: none;
}

:deep(.user-detail-tabs [data-slot="tabs-trigger"][data-state="active"]) {
  color: var(--el-color-primary);
  border-bottom-color: var(--el-color-primary);
}
</style>
